import { createModuleWorker } from '@w/runtime/module-worker'
import type { Baggage, Tel, TransportBaggage } from '@w/tel'
import type { PageEmbeddingsWorkerApi } from './page-embeddings-worker-api'
import type { WorkerBaggage, WorkerEnvelope } from './page-embeddings-worker-envelope'

/**
 * Structural (not Comlink.Remote<...>) so tests can inject a fake remote without a real Worker.
 */
type EmbeddingsRemote = {
  [K in 'embedDocument' | 'embedQuery']: (
    input: Parameters<PageEmbeddingsWorkerApi[K]>[0],
    baggage: WorkerBaggage | null,
  ) => Promise<WorkerEnvelope<number[]>>
}

/** Owned by the browser client; inference runs in the @w/runtime module worker primitive (real-Chromium proof: libs/platform/infra/runtime/src/module-worker.browser.test.ts); database tasks never await this worker. */
export function createPageEmbeddingsWorkerClient(cacheDir: string, spawn: () => Worker = spawnEmbeddingsWorker) {
  const worker = createModuleWorker<PageEmbeddingsWorkerApi>(spawn, { name: 'Search embeddings' })
  const embed = _createEmbed(worker.remote, cacheDir)
  return {
    embedDocument: (text: string, callTel: Tel) => embed('embedDocument', text, callTel),
    embedQuery: (text: string, callTel: Tel) => embed('embedQuery', text, callTel),
    close: () => { worker.close() },
  }
}

function spawnEmbeddingsWorker(): Worker {
  return new Worker(new URL('./page-embeddings-browser-worker.ts', import.meta.url), {
    type: 'module', name: 'flashcards-page-embeddings',
  })
}

/** Relays the worker envelope beneath the caller's operation. */
export function _createEmbed(remote: EmbeddingsRemote, cacheDir: string) {
  return function embed(method: 'embedDocument' | 'embedQuery', text: string, callTel: Tel): Promise<number[]> {
    return callTel.span(
      { name: `embeddings.${method}`, target: 'job', type: 'query', input: { textLength: text.length } },
      (spanTel) => _callWorker(remote, method, cacheDir, text, _transportBaggage(spanTel._getBaggage()), spanTel),
    )
  }
}

async function _callWorker(
  remote: EmbeddingsRemote,
  method: 'embedDocument' | 'embedQuery',
  cacheDir: string,
  text: string,
  baggage: TransportBaggage | null,
  spanTel: Tel,
): Promise<number[]> {
  const envelope = await remote[method]({ cacheDir, text }, baggage)
  if (envelope.telemetry) spanTel._insertBoundaryTrace(envelope.telemetry.trace, envelope.telemetry.metrics, envelope.telemetry.resource)
  if (envelope.ok) return envelope.result
  const error = new Error(envelope.error.message)
  error.name = envelope.error.name
  error.stack = envelope.error.stack
  throw error
}

function _transportBaggage(baggage: Baggage): TransportBaggage {
  const { requestId: _requestId, ...transport } = baggage
  return transport
}
